/**
 * The settings at the end of an Admin guide section: one field each, the
 * problem lines (in red) when a change cannot be saved, Save (only with a
 * valid change) and Back to defaults (fills the defaults in; Save still
 * sends them). Only the changed values go out; the toast says what happens.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import type { DropGenEngineSettingKey, DropGenEngineSettings } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { useSaveEngineSettings } from '../use-engine-settings';
import { changedPatch, defaultsDraft, defsOf, problemsOf, type SettingsDraft, valueOf } from './section-draft';
import { SettingField } from './SettingField';

interface Props {
  keys: readonly DropGenEngineSettingKey[];
  saved: DropGenEngineSettings;
}

export const SectionSettings: FC<Props> = ({ keys, saved }) => {
  const [draft, setDraft] = useState<SettingsDraft>({});
  const save = useSaveEngineSettings();
  const { success, error } = useToast();
  const patch = changedPatch(draft, saved, keys);
  const problems = problemsOf(patch);
  const changed = Object.keys(patch).length > 0;
  const onSave = async () => {
    try {
      await save.mutateAsync(patch);
      setDraft({});
      success('Engine settings saved', 'Every country uses them within a minute.');
    } catch (err) {
      error('The engine settings were not saved', getApiErrorMessage(err));
    }
  };
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/[0.03] px-3 py-2" data-testid="engine-settings">
      <p className="pb-1 text-[10px] font-semibold uppercase tracking-wider text-primary">Settings · every country</p>
      {defsOf(keys).map((def) => (
        <SettingField key={def.key} def={def} value={valueOf(draft, saved, def.key as DropGenEngineSettingKey)} onChange={(v) => setDraft((d) => ({ ...d, [def.key]: v }))} />
      ))}
      {problems.map((p) => (
        <p key={p} className="text-xs text-destructive" role="alert">{p}</p>
      ))}
      <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
        <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={() => setDraft(defaultsDraft(keys))}>
          <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Back to defaults
        </Button>
        <Button size="sm" className="h-7 gap-1.5 text-xs" disabled={!changed || problems.length > 0 || save.isPending} onClick={() => void onSave()}>
          <Save className="h-3.5 w-3.5" aria-hidden /> Save
        </Button>
      </div>
    </div>
  );
};
